/**
 * Live brain updates via SSE (EventSource), with short-interval poll fallback.
 * Cookie session is sent automatically (same-origin).
 */

const SSE_URL = '/api/paper/brain/stream'
const POLL_URL = '/api/paper/brain'
const FALLBACK_MS = 4000

/**
 * @param {{ onSnapshot: (snap: object) => void, onStatus?: (s: 'sse'|'poll'|'error'|'idle') => void }} opts
 */
export function subscribeBrainLive({ onSnapshot, onStatus } = {}) {
  let closed = false
  let es = null
  let pollTimer = null
  let mode = 'idle'

  function setStatus(s) {
    mode = s
    onStatus?.(s)
  }

  function apply(snap) {
    if (closed || !snap || snap.ok === false) return
    onSnapshot?.(snap)
  }

  async function pollOnce() {
    if (closed) return
    if (typeof document !== 'undefined' && document.hidden) return
    try {
      const res = await fetch(POLL_URL, {
        credentials: 'include',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      })
      if (res.status === 401) {
        setStatus('error')
        return
      }
      const data = await res.json()
      apply(data)
      if (mode !== 'sse') setStatus('poll')
    } catch {
      setStatus('error')
    }
  }

  function startPollFallback() {
    stopEs()
    if (pollTimer != null) return
    setStatus('poll')
    pollOnce()
    pollTimer = setInterval(pollOnce, FALLBACK_MS)
  }

  function stopPoll() {
    if (pollTimer != null) {
      clearInterval(pollTimer)
      pollTimer = null
    }
  }

  function stopEs() {
    if (es) {
      try {
        es.close()
      } catch {
        /* ignore */
      }
      es = null
    }
  }

  function startSse() {
    if (closed || typeof EventSource === 'undefined') {
      startPollFallback()
      return
    }
    stopPoll()
    stopEs()
    try {
      es = new EventSource(SSE_URL, { withCredentials: true })
    } catch {
      startPollFallback()
      return
    }
    setStatus('sse')
    es.addEventListener('brain', (ev) => {
      try {
        apply(JSON.parse(ev.data))
        setStatus('sse')
      } catch {
        /* ignore bad frame */
      }
    })
    es.onerror = () => {
      // Browser will retry EventSource; after repeated fails fall back to poll.
      stopEs()
      startPollFallback()
    }
  }

  function onVisibility() {
    if (document.hidden) {
      stopEs()
      stopPoll()
      setStatus('idle')
    } else if (!closed) {
      startSse()
    }
  }

  startSse()
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibility)
  }

  return {
    get mode() {
      return mode
    },
    close() {
      closed = true
      stopEs()
      stopPoll()
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility)
      }
      setStatus('idle')
    },
  }
}
